import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getReportSubscriptionCriteria } from '@/lib/reportSubscriptionsApi';
import { getGroupsForCompany } from '@/lib/api';
import { deserializeReportCriteria, serializeReportCriteria } from '@/lib/reportCriteria';
import { groupDateSpan, subscribeBlockReason } from '@/lib/reportSubscribeRules';
import { criteriaToReportsHydration } from '@/lib/reportPresetHydration';
import { ReportSubscribeDrawer } from '@/components/reports/ReportSubscribeDrawer';
import { ReportSubscriptionsManager } from '@/components/reports/ReportSubscriptionsManager';
import { cn } from '@/lib/utils';
import { getLabelForUser } from '@/lib/labels';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { Menu } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ReportsFiltersSidebar } from '@/components/reports/ReportsFiltersSidebar';
import { ReportsStatusBar } from '@/components/reports/ReportsStatusBar';
import { ReportsTabBar } from '@/components/reports/ReportsTabBar';
import { GroupAnalyticsPanel } from '@/components/reports/GroupAnalyticsPanel';
import { useFilters } from '@/contexts/FilterContext';
import { useRBAC } from '@/contexts/RBACContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useStationMetrics } from '@/hooks/useStationMetrics';
import { useReportFocus } from '@/hooks/useReportFocus';
import { bucketUnitStatuses } from '@/lib/reportStatusBuckets';
import { useUnitStatusOptional } from '@/contexts/UnitStatusContext';
import { useReportsLocalStorage } from '@/hooks/useReportsLocalStorage';
import { useExcelExport, type ReportType } from '@/hooks/useExcelExport';
import { useGroupDeployments } from '@/hooks/useGroupDeployments';
import type {
  ReportFilters,
  GroupAnalyticsFilters,
  ReportViewMode,
  ReportSummary,
  CompareState,
} from '@/types/reports';
import {
  serializeCompareState,
  deserializeCompareState,
} from '@/types/reports';
import { getDateRangeFromPreset, DEFAULT_COMPARE_STATE, createEmptyCompareSlot, COMPARE_SLOT_COLORS } from '@/types/reports';
import { CompareAnalyticsPanel } from '@/components/reports/CompareAnalyticsPanel';
import { CompareSidebar } from '@/components/reports/CompareSidebar';
import { MODEL_LABELS } from '@/lib/constants';
import type { Unit, Company } from '@/types';
import { ChevronLeft, ChevronRight, CalendarClock, Bell } from 'lucide-react';
import { trackReportTabChanged, trackReportExported, trackReportCompareSlotAdded } from '@/lib/analytics';

const DEFAULT_FILTERS: ReportFilters = {
  datePreset: 'last-30-days',
  customDateRange: { start: null, end: null },
  companyIds: [],
  groupIds: [],
};

// Get all model codes as the default selection
const ALL_MODEL_CODES = Object.keys(MODEL_LABELS);

const INITIAL_ANALYTICS_FILTERS: GroupAnalyticsFilters = {
  groupIds: [],
  modelCodes: ALL_MODEL_CODES, // All models selected by default
  granularity: 'weekly',
  periodCount: 4,
  useFullGroup: false,
  enabledMetrics: ['solarKwh', 'offGridPercent', 'co2Reduced'],
  showCharts: true,
  showStats: true,
  showUnitDetails: false,
  viewMode: 'charts',
};

export function Reports() {
  const isMobile = useIsMobile();
  // NOTE: selectedGroupIds/setSelectedGroupIds NOT used - Reports maintains its own groupIds in localStorage
  const { companies, groups, units, selectedCompanyIds, selectedIndustryTypes, selectedModels, setSelectedModels, setSelectedCompanyIds, setSelectedIndustryTypes, isLoading: filterContextLoading } = useFilters();
  const { user, isInternal, isSuperadmin, isAdmin, isCompanyAdmin } = useRBAC();
  // Matches the backend reportAdminScope gate (ISS-063): company-wide admins only —
  // a group_admin is refused (403) by the report-subscription routes.
  const canManageSubscriptions = isSuperadmin || isAdmin || isCompanyAdmin;
  const { labels = [] } = useSettingsContext();

  // LocalStorage hook for Reports-specific settings (includes groupIds, date range, granularity, etc.)
  // Reports page maintains its own group selection separate from Dashboard/Map
  const { getInitialState: getLocalStorageState, updateLocalState, clearStoredFilters, setHasSeenOnboarding, setHasSeenCompareOnboarding } = useReportsLocalStorage(user?.id);
  const stateInitialized = useRef(false);

  // B1 deep-link: /reports?preset=<subscriptionId>. When present, the subscription's
  // stored criteria hydrate the page with PRECEDENCE over localStorage. 'loading'
  // defers the localStorage init below until the fetch resolves (success → state
  // already hydrated; failure → fall through to localStorage).
  const [searchParams, setSearchParams] = useSearchParams();
  const [presetPhase, setPresetPhase] = useState<'idle' | 'loading' | 'done'>(
    () => (searchParams.get('preset') ? 'loading' : 'idle'),
  );
  const presetAttempted = useRef(false);
  // Read when the preset resolves (to open "All units" with the company's
  // productions selected); a ref so a groups refresh doesn't restart the fetch.
  const presetGroupsRef = useRef(groups);
  presetGroupsRef.current = groups;

  // Onboarding tooltip state - show only if user hasn't seen it before
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCompareOnboarding, setShowCompareOnboarding] = useState(false);

  // Sidebar ref for potential future use
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Compute group label based on selected industries
  // Only use 'Production' when ONLY film is selected
  // Use stable default during loading to prevent text blending
  const groupLabel = useMemo(() => {
    // Show 'Production' during initial load when data hasn't populated yet
    if (filterContextLoading || selectedIndustryTypes.length === 0) return 'Production';
    const industry = selectedIndustryTypes.length === 1 && selectedIndustryTypes[0] === 'film' ? 'film' : 'default';
    return getLabelForUser('group', 'user', industry, labels) || (industry === 'film' ? 'Production' : 'Group');
  }, [selectedIndustryTypes, labels, filterContextLoading]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Compare mode state - initialized from localStorage
  const [compareState, setCompareState] = useState<CompareState>(() => {
    const stored = localStorage.getItem('reports_compare_state');
    if (stored) {
      const deserialized = deserializeCompareState(stored);
      if (deserialized) return deserialized;
    }
    return DEFAULT_COMPARE_STATE;
  });

  // Sidebar collapse state (shared across all tabs)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('reports_sidebar_collapsed');
    return stored === 'true';
  });

  // Include All Deployments toggle (internal users only)
  // Shows data from all deployment periods with visual annotations
  const [includeAllDeployments, setIncludeAllDeployments] = useState(() => {
    const stored = localStorage.getItem('reports_include_all_deployments');
    return stored === 'true';
  });

  // B5: subscribe / manage drawers.
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [subscriptionsRefreshKey, setSubscriptionsRefreshKey] = useState(0);

  // Persist sidebar collapse state
  useEffect(() => {
    localStorage.setItem('reports_sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Persist includeAllDeployments state
  useEffect(() => {
    localStorage.setItem('reports_include_all_deployments', String(includeAllDeployments));
  }, [includeAllDeployments]);

  // Persist compare state to localStorage
  useEffect(() => {
    localStorage.setItem('reports_compare_state', serializeCompareState(compareState));
  }, [compareState]);

  // Compare slot handlers
  const handleAddSlot = useCallback(() => {
    if (compareState.slots.length >= 4) return;
    const newSlot = createEmptyCompareSlot(compareState.slots.length);
    trackReportCompareSlotAdded(compareState.slots.length);
    setCompareState(prev => ({
      ...prev,
      slots: [...prev.slots, newSlot],
    }));
  }, [compareState.slots.length]);

  const handleRemoveSlot = useCallback((slotId: string) => {
    setCompareState(prev => {
      const newSlots = prev.slots.filter(s => s.id !== slotId);
      // Recolor remaining slots to maintain sequence
      const recoloredSlots = newSlots.map((slot, index) => ({
        ...slot,
        color: COMPARE_SLOT_COLORS[index],
      }));
      return { ...prev, slots: recoloredSlots };
    });
  }, []);

  const handleReorderSlots = useCallback((newSlots: typeof compareState.slots) => {
    // Recolor to maintain sequence after reorder
    const recoloredSlots = newSlots.map((slot, index) => ({
      ...slot,
      color: COMPARE_SLOT_COLORS[index],
    }));
    setCompareState(prev => ({ ...prev, slots: recoloredSlots }));
  }, []);

  // Filter companies by selected industry (industry type)
  // During loading, show all companies to prevent empty state flicker
  // Once FilterContext finishes loading, apply the industry type filter
  const filteredCompanies = useMemo(() => {
    if (filterContextLoading) return companies; // Show all during load
    if (selectedIndustryTypes.length === 0) return []; // No industries selected = no data
    return companies.filter((a: Company) => a.industry && selectedIndustryTypes.includes(a.industry));
  }, [companies, selectedIndustryTypes, filterContextLoading]);

  // Filter groups by selected companies (for sidebar)
  // Only show groups for companies that are both selected AND in the filtered list
  const filteredGroups = useMemo(() => {
    if (filteredCompanies.length === 0) return [];
    // Get company IDs that are both selected and in filtered list
    const validCompanyIds = selectedCompanyIds.filter((id: string) =>
      filteredCompanies.some((a: Company) => a.id === id)
    );
    if (validCompanyIds.length === 0) return [];
    const result = groups.filter(g => validCompanyIds.includes(g.company_id));
    return result;
  }, [groups, selectedCompanyIds, filteredCompanies]);

  // Get selected company for logo display
  // If only one company is selected, use that; otherwise use first selected or null
  const selectedCompany = useMemo(() => {
    const validSelectedIds = selectedCompanyIds.filter((id: string) =>
      filteredCompanies.some((a: Company) => a.id === id)
    );
    if (validSelectedIds.length === 1) {
      return filteredCompanies.find((a: Company) => a.id === validSelectedIds[0]) || null;
    }
    if (validSelectedIds.length > 1) {
      return filteredCompanies.find((a: Company) => a.id === validSelectedIds[0]) || null;
    }
    return null;
  }, [selectedCompanyIds, filteredCompanies]);

  // Initialize state from localStorage > defaults (NO URL params)
  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [analyticsFilters, setAnalyticsFilters] = useState<GroupAnalyticsFilters>(INITIAL_ANALYTICS_FILTERS);

  // Determine if selected groups are film industry (for label counting logic)
  // This is based on actual selected groups, not header filter
  const isFilmIndustry = useMemo(() => {
    if (analyticsFilters.groupIds.length === 0) return false;
    const selectedGroups = groups.filter(g => analyticsFilters.groupIds.includes(g.id));
    const companyIds = [...new Set(selectedGroups.map(g => g.company_id))];
    const industries = companies
      .filter((a: Company) => companyIds.includes(a.id) && a.industry)
      .map((a: Company) => a.industry);
    // Only true if ALL selected groups are film industry
    return industries.length > 0 && industries.every(ind => ind === 'film');
  }, [groups, analyticsFilters.groupIds, companies]);

  // B5: the current report's criteria (what a new subscription reproduces) + the
  // companies the user may subscribe for (their current selection).
  // Live unit statuses (polled every 15 s) — the Unit Status card and its
  // click-to-focus lists follow these, not the one-time fleet load.
  const liveStatuses = useUnitStatusOptional()?.statuses;

  // Compute real summary statistics from actual units data
  const reportSummary = useMemo<ReportSummary | null>(() => {
    // Need units data to compute real stats
    if (!units || units.length === 0) return null;

    // If no models are selected (user cleared all), return zero counts
    // This implements the "no models checked = 0 units" behavior
    if (analyticsFilters.modelCodes.length === 0) {
      return {
        totalUnits: 0,
        totalEnergyGenerated: 0,
        totalOffGridPercentage: 0,
        totalCO2Reduced: 0,
        averageBatteryLevel: 0,
        onlineUnits: 0,
        offlineUnits: 0,
        idleUnits: 0,
        modelCounts: {},
        statusUnitIds: { online: [], offline: [], idle: [] },
      };
    }

    // Filter units by selected filters (companies, groups, models, industry types)
    // Use analyticsFilters.groupIds (Reports-specific) not selectedGroupIds (FilterContext)
    const filteredUnits = (units as Unit[]).filter(unit => {
      // Company filter
      if (selectedCompanyIds.length > 0 && !selectedCompanyIds.includes(unit.company_id)) {
        return false;
      }
      // Group filter (include unassigned) - use Reports-specific groupIds
      if (analyticsFilters.groupIds.length > 0) {
        const unassignedId = `__unassigned_${unit.company_id}__`;
        const hasMatchingGroup = unit.primary_group_id
          ? analyticsFilters.groupIds.includes(unit.primary_group_id)
          : analyticsFilters.groupIds.includes(unassignedId);
        if (!hasMatchingGroup) return false;
      }
      // Model filter - always apply (if no models selected, already returned above)
      const modelCode = unit.unit_code?.substring(0, 3) || '';
      if (!analyticsFilters.modelCodes.includes(modelCode)) return false;
      // Industry type filter
      if (selectedIndustryTypes.length > 0 && unit.industry_type) {
        if (!selectedIndustryTypes.includes(unit.industry_type)) return false;
      }
      return true;
    });

    // Status counts AND the click-to-focus unit lists from one bucketing,
    // preferring the live polled status over the one-time fleet load.
    const statusUnitIds = bucketUnitStatuses(filteredUnits as Unit[], liveStatuses);

    // Count by model (first 3 chars of unit_code)
    const modelCounts: Record<string, number> = {};
    filteredUnits.forEach(unit => {
      const modelCode = unit.unit_code?.substring(0, 3) || 'UNK';
      modelCounts[modelCode] = (modelCounts[modelCode] || 0) + 1;
    });

    return {
      // Use units data directly for unit count (same source as dropdowns) - bypasses broken RPC
      totalUnits: filteredUnits.length,
      totalEnergyGenerated: 0, // Not available from current data
      totalOffGridPercentage: 0, // Not available from current data
      totalCO2Reduced: 0, // Not available from current data
      averageBatteryLevel: 0, // Not available from current data
      onlineUnits: statusUnitIds.online.length,
      offlineUnits: statusUnitIds.offline.length,
      idleUnits: statusUnitIds.idle.length,
      modelCounts,
      statusUnitIds,
    };
  }, [units, selectedCompanyIds, analyticsFilters.groupIds, analyticsFilters.modelCodes, selectedIndustryTypes, liveStatuses]);

  // Click-to-focus from the sidebar breakdown cards: narrows what the report
  // queries (station metrics, charts, export) without changing the sidebar's
  // model selection, so every model row stays clickable.
  const {
    focusedModel,
    reportModelCodes,
    toggleModel: toggleModelFocus,
    clearFocus: clearModelFocus,
    focusModelCodes,
    focusedStatus,
    reportUnitIds,
    toggleStatus: toggleStatusFocus,
    clearStatus: clearStatusFocus,
  } = useReportFocus(analyticsFilters.modelCodes, reportSummary?.statusUnitIds);
  // Unit count shown by the panel: every focus narrows it (model ∩ status).
  const focusedUnitCount = useMemo(() => {
    if (!reportSummary) return 0;
    if (!reportUnitIds) {
      return focusedModel ? (reportSummary.modelCounts[focusedModel] ?? 0) : reportSummary.totalUnits;
    }
    if (!focusedModel) return reportUnitIds.length;
    const ids = new Set(reportUnitIds);
    return (units as Unit[]).filter(u => ids.has(u.id) && u.unit_code?.substring(0, 3) === focusedModel).length;
  }, [reportSummary, reportUnitIds, focusedModel, units]);

  const reportAnalyticsFilters = useMemo<GroupAnalyticsFilters>(
    () => (focusedModel ? { ...analyticsFilters, modelCodes: reportModelCodes } : analyticsFilters),
    [analyticsFilters, focusedModel, reportModelCodes],
  );

  const subscribeGroupSpan = useMemo(
    () => groupDateSpan(analyticsFilters.groupIds, groups),
    [analyticsFilters.groupIds, groups],
  );
  const currentCriteria = useMemo(() => serializeReportCriteria({
    reportType: analyticsFilters.viewMode,
    companyIds: selectedCompanyIds,
    groupIds: analyticsFilters.groupIds,
    // A focused model (click-to-focus) is what the report shows, so a
    // subscription created while focused covers only that model.
    modelCodes: focusModelCodes(selectedModels),
    industryTypes: selectedIndustryTypes,
    datePreset: filters.datePreset,
    customDateRange: filters.customDateRange,
    granularity: analyticsFilters.granularity,
    includeAllDeployments,
    // "Use production dates" is stored as the productions' explicit dates.
    useFullGroup: analyticsFilters.useFullGroup,
    groupDateRange: subscribeGroupSpan,
    compareSlots: compareState.slots,
  }), [analyticsFilters.viewMode, analyticsFilters.groupIds, analyticsFilters.granularity,
    analyticsFilters.useFullGroup, subscribeGroupSpan,
    selectedCompanyIds, selectedModels, focusModelCodes, selectedIndustryTypes, filters.datePreset,
    filters.customDateRange, includeAllDeployments, compareState.slots]);
  // Why the current report can't be scheduled (compare, an empty selection, a
  // status focus, unresolvable production dates), shown on Subscribe and in
  // the drawer.
  const subscribeDisabledReason = useMemo(() => subscribeBlockReason(currentCriteria, {
    groupDateRange: subscribeGroupSpan,
    hasGroup: analyticsFilters.groupIds.length > 0,
    statusFocused: !!focusedStatus,
  }), [currentCriteria, subscribeGroupSpan, analyticsFilters.groupIds.length, focusedStatus]);

  const subscribeCompanyOptions = useMemo(
    () => companies.filter((c: Company) => selectedCompanyIds.includes(c.id)).map((c: Company) => ({ id: c.id, name: c.name })),
    [companies, selectedCompanyIds],
  );

  // One-time cleanup of legacy storage keys (security fix + migration)
  useEffect(() => {
    // Remove legacy storage formats
    localStorage.removeItem('ht-report-filters');

    // Clean up non-user-scoped reports keys that may have leaked data
    // These keys are now user-scoped in useReportsLocalStorage
    const legacyKeys = [
      'reports_industryTypes',    // Now handled by FilterContext
      'reports_modelCodes',    // Now handled by FilterContext
      'reports_datePreset',    // Now user-scoped
      'reports_customDateRange',
      'reports_granularity',
      'reports_periodCount',
      'reports_useFullGroup',
      'reports_enabledMetrics',
      'reports_viewMode',
      'reports_lastVisit',
    ];
    legacyKeys.forEach(key => localStorage.removeItem(key));
  }, []);

  // Initialize settings from localStorage on mount
  // Reports page maintains its own groupIds separate from Dashboard/Map FilterContext
  useEffect(() => {
    // Skip if already initialized
    if (stateInitialized.current) return;

    // B1: a deep-link preset takes precedence — defer localStorage init until the
    // preset fetch resolves. On success it sets stateInitialized (this returns
    // above next run); on failure presetPhase flips to 'done' and we hydrate here.
    if (presetPhase === 'loading') return;

    // Wait for FilterContext to finish loading — NOT for groups to be non-empty.
    // A viewer with no groups (or who cleared company selections) keeps groups
    // empty with isLoading already false; gating on length would hang init forever.
    if (filterContextLoading) return;

    const localState = getLocalStorageState();

    // Restore presentation settings from localStorage (if any)
    const restoredFilters: ReportFilters = {
      ...DEFAULT_FILTERS,
      ...(localState.datePreset && { datePreset: localState.datePreset }),
      ...(localState.customDateRange && { customDateRange: localState.customDateRange }),
    };

    // Use Reports-specific groupIds from localStorage
    // Start with empty selection if no localStorage value (user must explicitly select)
    const restoredAnalyticsFilters: GroupAnalyticsFilters = {
      ...INITIAL_ANALYTICS_FILTERS,
      groupIds: localState.groupIds ?? [], // Reports-specific, default to empty
      modelCodes: selectedModels, // From FilterContext (shared with Dashboard/Map)
      ...(localState.granularity && { granularity: localState.granularity }),
      ...(localState.periodCount !== undefined && { periodCount: localState.periodCount }),
      ...(localState.useFullGroup !== undefined && { useFullGroup: localState.useFullGroup }),
      ...(localState.enabledMetrics && { enabledMetrics: localState.enabledMetrics }),
      ...(localState.viewMode && { viewMode: localState.viewMode }),
    };

    setFilters(restoredFilters);
    setAnalyticsFilters(restoredAnalyticsFilters);

    // Use queueMicrotask to ensure React's state batch completes before marking initialized
    queueMicrotask(() => {
      stateInitialized.current = true;

      // Show onboarding tooltip when no groups selected (always, not just first time)
      if ((localState.groupIds?.length ?? 0) === 0) {
        setShowOnboarding(true);
      }
    });
  }, [getLocalStorageState, selectedModels, filterContextLoading, presetPhase]);

  // B1 deep-link hydration: fetch the subscription's criteria and apply them with
  // precedence over localStorage. m4 authz is enforced server-side (404 if the
  // viewer isn't in the subscription's company); a 404/parse failure degrades to
  // the normal localStorage view rather than dead-ending. Runs once.
  useEffect(() => {
    const presetId = searchParams.get('preset');
    if (!presetId || presetPhase !== 'loading' || presetAttempted.current) return;
    // Wait for FilterContext to finish loading, NOT for a non-empty group list —
    // a no-groups viewer would otherwise never resolve the preset (deadlocking the
    // localStorage init, which defers while presetPhase is 'loading').
    if (filterContextLoading) return;
    presetAttempted.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { criteria: raw } = await getReportSubscriptionCriteria(presetId);
        const criteria = deserializeReportCriteria(raw);
        if (cancelled) return;
        if (!criteria) {
          toast.warn('This report link is out of date and could not be opened.');
        } else {
          // "All units in <company>" opens with that company's productions
          // selected. The shared group list only holds the companies already
          // selected here, so read the company's own list first; if that read
          // fails, open with nothing selected (the "choose a production" view)
          // rather than a report that silently shows only unassigned units.
          let hydrationGroups: { id: string; company_id: string | null }[] | null = presetGroupsRef.current;
          if (criteria.groupScope === 'all' && criteria.companyIds.length === 1) {
            hydrationGroups = await getGroupsForCompany(criteria.companyIds[0]).catch(() => null);
            if (cancelled) return;
          }
          const h = criteriaToReportsHydration(criteria, hydrationGroups);
          // Shared header filters. Group selection is Reports-local (analytics
          // + filters below) — do NOT push it into the shared FilterContext
          // group state, which Dashboard/Map also consume.
          setSelectedCompanyIds(h.companyIds);
          setSelectedIndustryTypes(h.industryTypes);
          setSelectedModels(h.models);
          setFilters(prev => ({ ...prev, ...h.filters }));
          setAnalyticsFilters(prev => ({ ...prev, ...h.analytics }));
          setIncludeAllDeployments(h.includeAllDeployments);
          // Preset applied — block the localStorage init from overriding it.
          stateInitialized.current = true;
        }
      } catch {
        if (!cancelled) {
          toast.error("You don't have access to this report, or it no longer exists.");
        }
      } finally {
        if (!cancelled) {
          // Strip ?preset so a refresh / state-save doesn't re-trigger; keep other params.
          setSearchParams(
            prev => {
              const next = new URLSearchParams(prev);
              next.delete('preset');
              return next;
            },
            { replace: true },
          );
          setPresetPhase('done');
        }
      }
    })();

    return () => {
      cancelled = true;
      // StrictMode (dev) runs setup→cleanup→setup; allow the second setup to
      // re-attempt rather than latching on the first (abandoned) run.
      presetAttempted.current = false;
    };
  }, [
    searchParams,
    presetPhase,
    filterContextLoading,
    setSelectedCompanyIds,
    setSelectedIndustryTypes,
    setSelectedModels,
    setIncludeAllDeployments,
    setSearchParams,
  ]);

  // Dismiss onboarding handler
  const handleDismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
    setHasSeenOnboarding();
  }, [setHasSeenOnboarding]);

  // Dismiss compare onboarding handler
  const handleDismissCompareOnboarding = useCallback(() => {
    setShowCompareOnboarding(false);
    setHasSeenCompareOnboarding();
  }, [setHasSeenCompareOnboarding]);

  // Always show onboarding tooltip when no groups are selected
  useEffect(() => {
    if (!stateInitialized.current) return;
    if (analyticsFilters.groupIds.length === 0) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [analyticsFilters.groupIds.length]);

  // Always show compare onboarding when in compare mode with no slots
  useEffect(() => {
    if (!stateInitialized.current) return;
    if (analyticsFilters.viewMode === 'compare' && compareState.slots.length === 0) {
      setShowCompareOnboarding(true);
    } else {
      setShowCompareOnboarding(false);
    }
  }, [analyticsFilters.viewMode, compareState.slots.length]);

  // Check if we have any group data selected
  const hasGroupSelected = analyticsFilters.groupIds.length > 0;

  // Sync Reports settings to localStorage for persistence across refresh
  // Reports has its own groupIds separate from Dashboard/Map
  useEffect(() => {
    // Skip during initial state restoration
    if (!stateInitialized.current) return;

    updateLocalState({
      groupIds: analyticsFilters.groupIds, // Reports-specific group selection
      datePreset: filters.datePreset,
      customDateRange: filters.customDateRange,
      granularity: analyticsFilters.granularity,
      periodCount: analyticsFilters.periodCount,
      useFullGroup: analyticsFilters.useFullGroup,
      enabledMetrics: analyticsFilters.enabledMetrics,
      viewMode: analyticsFilters.viewMode,
    });
  }, [
    analyticsFilters.groupIds,
    filters.datePreset,
    filters.customDateRange,
    analyticsFilters.granularity,
    analyticsFilters.periodCount,
    analyticsFilters.useFullGroup,
    analyticsFilters.enabledMetrics,
    analyticsFilters.viewMode,
    updateLocalState,
  ]);

  // Sync company selection from FilterContext
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      companyIds: selectedCompanyIds,
    }));
  }, [selectedCompanyIds]);

  // NOTE: groupIds are NOT synced from FilterContext - Reports maintains its own selection
  // This allows Reports to have different group selections than Dashboard/Map

  // Sync model selection from FilterContext to analytics filters
  useEffect(() => {
    setAnalyticsFilters(prev => ({
      ...prev,
      modelCodes: selectedModels,
    }));
  }, [selectedModels]);

  // Get all selected root groups (productions) for date range union
  const selectedRootGroups = useMemo(() => {
    if (analyticsFilters.groupIds.length === 0) return [];
    const selectedGroupSet = new Set(analyticsFilters.groupIds);
    const rootProductions = groups.filter(g => !g.parent_group_id);
    return rootProductions.filter(root => {
      if (selectedGroupSet.has(root.id)) return true;
      const children = groups.filter(g => g.parent_group_id === root.id);
      return children.some(child => selectedGroupSet.has(child.id));
    });
  }, [analyticsFilters.groupIds, groups]);

  // Calculate effective date range from preset, custom selection, or production dates
  const effectiveDateRange = useMemo(() => {
    // If useFullGroup is true, compute union of date ranges across all selected root groups
    if (analyticsFilters.useFullGroup && selectedRootGroups.length > 0) {
      const parseLocalDate = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };
      let earliestStart: Date | null = null;
      let latestEnd: Date | null = null;
      selectedRootGroups.forEach(g => {
        if (g.start_date) {
          const start = parseLocalDate(g.start_date);
          if (!earliestStart || start < earliestStart) earliestStart = start;
        }
        if (g.end_date) {
          const end = parseLocalDate(g.end_date);
          if (!latestEnd || end > latestEnd) latestEnd = end;
        }
      });
      if (earliestStart && latestEnd) {
        return { start: earliestStart, end: latestEnd };
      }
    }

    if (filters.datePreset === 'custom' && filters.customDateRange.start && filters.customDateRange.end) {
      return {
        start: filters.customDateRange.start,
        end: filters.customDateRange.end,
      };
    }
    const presetRange = getDateRangeFromPreset(filters.datePreset);
    return presetRange;
  }, [filters.datePreset, filters.customDateRange, analyticsFilters.useFullGroup, selectedRootGroups]);


  // Fetch station metrics from Supabase RPC - this is the authoritative source for unit counts
  // Lifted to Reports.tsx so we can share the data with both sidebar and panel
  const {
    data: stationMetricsData,
    isLoading: isLoadingMetrics,
  } = useStationMetrics({
    groupIds: analyticsFilters.groupIds,
    dateRange: effectiveDateRange,
    modelCodes: reportModelCodes,
    industryTypes: selectedIndustryTypes,
    unitIds: reportUnitIds,
    enabled: analyticsFilters.groupIds.length > 0,
    includeAllDeployments: isInternal && includeAllDeployments,
  });

  // Deployment history for chart annotations (internal users only)
  const {
    deployments: groupDeployments,
  } = useGroupDeployments({
    groupIds: analyticsFilters.groupIds,
    startDate: effectiveDateRange.start,
    endDate: effectiveDateRange.end,
    enabled: isInternal && includeAllDeployments && analyticsFilters.groupIds.length > 0,
  });

  // Excel export hook
  const {
    exportExcel,
    isExporting,
  } = useExcelExport({
    groupIds: analyticsFilters.groupIds,
    modelCodes: reportModelCodes,
    industryTypes: selectedIndustryTypes,
    unitIds: reportUnitIds,
    startDate: effectiveDateRange.start,
    endDate: effectiveDateRange.end,
    granularity: analyticsFilters.granularity,
    compareSlots: compareState.slots,
    includeAllDeployments: isInternal && includeAllDeployments,
  });


  const handleFiltersChange = (newFilters: ReportFilters) => {
    setFilters(newFilters);
  };

  const handleAnalyticsFiltersChange = (newFilters: GroupAnalyticsFilters) => {
    setAnalyticsFilters(newFilters);

    // NOTE: groupIds are NOT synced to FilterContext - Reports maintains its own selection
    // This allows Reports to have different group selections than Dashboard/Map

    // Sync model selection changes back to FilterContext (models are shared)
    if (JSON.stringify(newFilters.modelCodes) !== JSON.stringify(analyticsFilters.modelCodes)) {
      setSelectedModels(newFilters.modelCodes);
    }
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAnalyticsFilters(INITIAL_ANALYTICS_FILTERS);
    // Reset models in FilterContext (shared with Dashboard/Map)
    setSelectedModels(ALL_MODEL_CODES);
    // Clear all localStorage keys for reports (includes groupIds)
    clearStoredFilters();
  };

  const handleViewModeChange = (viewMode: ReportViewMode) => {
    trackReportTabChanged(viewMode);
    setAnalyticsFilters(prev => ({ ...prev, viewMode }));
  };

  const hasActiveFilters =
    filters.datePreset !== 'last-30-days' ||
    filters.customDateRange.start !== null ||
    filters.customDateRange.end !== null ||
    analyticsFilters.granularity !== 'weekly' ||
    analyticsFilters.periodCount !== 4 ||
    analyticsFilters.useFullGroup ||
    analyticsFilters.groupIds.length > 0;

  // Filters sidebar content (shared between desktop and mobile)
  // Determine if sidebar should stretch to fill (for empty state alignment)
  // Stretch for: no models selected OR compare mode with no/empty slots
  const sidebarShouldStretch = analyticsFilters.modelCodes.length === 0 ||
    (analyticsFilters.viewMode === 'compare' && compareState.slots.length === 0);

  const filtersSidebar = (
    <ReportsFiltersSidebar
      filters={filters}
      onFiltersChange={handleFiltersChange}
      onResetFilters={handleResetFilters}
      hasActiveFilters={hasActiveFilters}
      summary={reportSummary ?? undefined}
      groups={filteredGroups}
      companies={filteredCompanies}
      analyticsFilters={analyticsFilters}
      onAnalyticsFiltersChange={handleAnalyticsFiltersChange}
      selectedIndustryTypes={selectedIndustryTypes}
      showOnboarding={showOnboarding}
      onDismissOnboarding={handleDismissOnboarding}
      stretchToFill={sidebarShouldStretch}
      focusedModel={focusedModel}
      onToggleModelFocus={toggleModelFocus}
      onClearModelFocus={clearModelFocus}
      focusedStatus={focusedStatus}
      onToggleStatusFocus={toggleStatusFocus}
      onClearStatusFocus={clearStatusFocus}
    />
  );

  // Action handlers
  const handleExportExcel = async () => {
    // Map view mode to report type
    const reportTypeMap: Record<ReportViewMode, ReportType> = {
      summary: 'summary',
      charts: 'charts',
      compare: 'compare',
    };
    const reportType = reportTypeMap[analyticsFilters.viewMode];
    trackReportExported({ format: 'excel', group_count: analyticsFilters.groupIds.length, date_range: filters.datePreset });
    try {
      await exportExcel(reportType);
      toast.success('Excel report downloaded successfully');
    } catch {
      // Error toast is already shown by useExcelExport hook
    }
  };

  const handleExportPdf = () => {
    toast.info('PDF export coming soon');
    // Future: Generate PDF using server-side Playwright
  };

  const handlePrint = () => {
    toast.info('Opening print dialog...');
    window.print();
  };

  const handleShare = () => {
    toast.info('Report sharing coming soon');
    // Future: Open share dialog with link generation
  };

  const handleEmail = () => {
    toast.info('Email reports coming soon');
    // Future: Open email composition modal
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header mobileTitle="Reports" />

      {/* Secondary header bar with RBAC filters and action buttons */}
      {!isMobile && (
        <ReportsStatusBar
          onExportExcel={handleExportExcel}
          isExporting={isExporting}
          onExportPdf={handleExportPdf}
          onPrint={handlePrint}
          onShare={handleShare}
          onEmail={handleEmail}
          onSubscribe={() => setSubscribeOpen(true)}
          subscribeDisabledReason={subscribeDisabledReason}
          onManageSubscriptions={() => setManageOpen(true)}
          viewMode={analyticsFilters.viewMode}
          activeSections={compareState.activeSections}
          onSectionsChange={(sections) => setCompareState(prev => ({ ...prev, activeSections: sections }))}
          isInternalUser={isInternal}
          includeAllDeployments={includeAllDeployments}
          onIncludeAllDeploymentsChange={setIncludeAllDeployments}
        />
      )}

      <main className="flex-1 overflow-hidden">
        {/* Mobile: extra right padding (pr-5) to prevent scrollbar from overlapping cards */}
        <div className="h-full pl-4 pr-5 pt-4 pb-4 md:p-6 overflow-y-auto">
          {/* Mobile: Filter Toggle + Content */}
          {isMobile ? (
            <div className="space-y-4">
              {/* Tab Bar - above filters on mobile too */}
              <ReportsTabBar
                viewMode={analyticsFilters.viewMode}
                onViewModeChange={handleViewModeChange}
              />

              {/* Mobile subscribe/manage — ReportsStatusBar (which carries these on
                  desktop) is not rendered on mobile, so surface them here. */}
              {canManageSubscriptions && (
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <Button
                      variant="outline" size="sm" className="flex-1 gap-1.5"
                      onClick={() => setSubscribeOpen(true)}
                      disabled={!!subscribeDisabledReason}
                    >
                      <CalendarClock className="h-4 w-4" />
                      Subscribe
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setManageOpen(true)}>
                      <Bell className="h-4 w-4" />
                      Subscriptions
                    </Button>
                  </div>
                  {subscribeDisabledReason && (
                    <p className="text-xs text-muted-foreground">{subscribeDisabledReason}</p>
                  )}
                </div>
              )}

              {/* Mobile Filter Toggle */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start gap-2",
                      !hasGroupSelected && "animate-greenPulse border-[#8FB560]"
                    )}
                  >
                    <Menu className="h-4 w-4" />
                    Filters & Date Range
                    {hasActiveFilters && (
                      <span className="ml-auto px-1.5 py-0.5 text-xs rounded-full bg-[#8FB560] text-black font-medium">
                        Active
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[100dvh] px-4 pt-4 pb-6">
                  <SheetHeader className="mb-3">
                    <SheetTitle className="text-lg">Report Filters</SheetTitle>
                  </SheetHeader>
                  <div className="overflow-y-auto max-h-[calc(100dvh-70px)]">
                    {filtersSidebar}
                  </div>
                </SheetContent>
              </Sheet>

              {/* Production Analytics - logo in top right, tab bar handles view switching */}
              <GroupAnalyticsPanel
                groups={groups}
                filters={reportAnalyticsFilters}
                showCharts={analyticsFilters.showCharts}
                showStats={analyticsFilters.showStats}
                dateRange={effectiveDateRange}
                selectedCompany={selectedCompany}
                groupLabel={groupLabel}
                isFilmIndustry={isFilmIndustry}
                stationMetricsData={stationMetricsData}
                isLoadingMetrics={isLoadingMetrics}
                unitCount={focusedUnitCount}
                unitIds={reportUnitIds}
                isContextLoading={filterContextLoading}
                noModelsSelected={analyticsFilters.modelCodes.length === 0}
                deployments={groupDeployments}
                showDeploymentAnnotations={isInternal && includeAllDeployments}
              />
            </div>
          ) : (
            /* Desktop: All Modes - Unified layout with collapsible sidebar */
            <div className={cn(
              "grid gap-6 max-w-[1800px] mx-auto transition-all duration-300",
              sidebarCollapsed ? "grid-cols-1" : "grid-cols-[288px_1fr]",
              sidebarShouldStretch ? "items-stretch" : "items-start"
            )}>
              {/* Collapsible Sidebar with Tab Bar */}
              <div
                ref={sidebarRef}
                className={cn(
                  "flex flex-col gap-4 relative transition-all duration-300",
                  !sidebarShouldStretch && "sticky top-0",
                  sidebarCollapsed && "absolute left-0 top-0 -translate-x-full opacity-0 pointer-events-none"
                )}
              >
                {/* Sidebar Toggle Button - Matches SettingsSidebar style (visible when sidebar is shown) */}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="absolute -right-3 top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-border hover:bg-accent rounded-full flex items-center justify-center transition-colors shadow-md"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="h-3 w-3" />
                </button>
                <ReportsTabBar
                  viewMode={analyticsFilters.viewMode}
                  onViewModeChange={handleViewModeChange}
                />
                {/* Show CompareSidebar in compare mode, filtersSidebar otherwise */}
                {analyticsFilters.viewMode === 'compare' ? (
                  <CompareSidebar
                    compareState={compareState}
                    onCompareStateChange={setCompareState}
                    slots={compareState.slots}
                    onAddSlot={handleAddSlot}
                    onRemoveSlot={handleRemoveSlot}
                    onReorderSlots={handleReorderSlots}
                    groups={filteredGroups}
                    companies={filteredCompanies}
                    selectedIndustryTypes={selectedIndustryTypes}
                    groupLabel={groupLabel}
                    showOnboarding={showCompareOnboarding}
                    onDismissOnboarding={handleDismissCompareOnboarding}
                  />
                ) : (
                  filtersSidebar
                )}
              </div>

              {/* Sidebar Toggle Button - Fixed to left edge (visible when sidebar is collapsed) */}
              {sidebarCollapsed && (
                <button
                  onClick={() => setSidebarCollapsed(false)}
                  className="fixed left-0 top-1/2 -translate-y-1/2 z-50 w-6 h-6 bg-border hover:bg-accent rounded-full flex items-center justify-center transition-colors shadow-md ml-2"
                  aria-label="Expand sidebar"
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
              )}

              {/* Main Content Area */}
              <div className="min-w-0">
                {analyticsFilters.viewMode === 'compare' ? (
                  <CompareAnalyticsPanel
                    compareState={compareState}
                    onCompareStateChange={setCompareState}
                    onAddSlot={handleAddSlot}
                    onRemoveSlot={handleRemoveSlot}
                    groups={filteredGroups}
                    companies={filteredCompanies}
                    selectedIndustryTypes={selectedIndustryTypes}
                    groupLabel={groupLabel}
                    dateRange={effectiveDateRange}
                    selectedCompany={selectedCompany}
                  />
                ) : (
                  <GroupAnalyticsPanel
                    groups={groups}
                    filters={reportAnalyticsFilters}
                    showCharts={analyticsFilters.showCharts}
                    showStats={analyticsFilters.showStats}
                    dateRange={effectiveDateRange}
                    selectedCompany={selectedCompany}
                    groupLabel={groupLabel}
                    isFilmIndustry={isFilmIndustry}
                    stationMetricsData={stationMetricsData}
                    isLoadingMetrics={isLoadingMetrics}
                    unitCount={focusedUnitCount}
                unitIds={reportUnitIds}
                    isContextLoading={filterContextLoading}
                    noModelsSelected={analyticsFilters.modelCodes.length === 0}
                    deployments={groupDeployments}
                    showDeploymentAnnotations={isInternal && includeAllDeployments}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* B5: subscribe to the current report + manage owned subscriptions. */}
      <ReportSubscribeDrawer
        open={subscribeOpen}
        onOpenChange={setSubscribeOpen}
        criteria={currentCriteria}
        companyOptions={subscribeCompanyOptions}
        groups={groups}
        blockReason={subscribeDisabledReason}
        currentUserId={user?.id}
        onCreated={() => setSubscriptionsRefreshKey((k) => k + 1)}
      />
      <ReportSubscriptionsManager
        open={manageOpen}
        onOpenChange={setManageOpen}
        refreshKey={subscriptionsRefreshKey}
      />
    </div>
  );
}
